import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSEO, { SITE_URL } from '@/hooks/useSEO';

export default function SafetyPage() {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language || 'en';

  useSEO({
    title: t('metaSafetyTitle'),
    description: t('metaSafetyDesc'),
    canonical: `${SITE_URL}/${currentLang}/safety`,
    lang: currentLang,
    ogType: 'website',
    ogTitle: t('metaSafetyTitle'),
    ogDesc: t('metaSafetyDesc'),
    ogUrl: `${SITE_URL}/${currentLang}/safety`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How can I stay safe when shopping on AllChinaBuy?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Always verify HTTPS connections, use strong unique passwords, enable two-factor authentication, research sellers before purchasing, and use secure payment methods with buyer protection.',
          },
        },
        {
          '@type': 'Question',
          name: 'What should I do if I encounter a suspicious seller?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Do not complete the transaction. Report the seller to the platform, save all communications, and consider using a different seller with verified reviews and ratings.',
          },
        },
      ],
    },
  });

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  const safetyTopics = [
    {
      icon: 'ri-lock-line',
      title: 'Website Security',
      desc: 'Always verify you are browsing the official AllChinaBuy website. Look for HTTPS in the URL bar and check the SSL certificate before entering any login credentials.',
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'Account Security',
      desc: 'Use strong, unique passwords for your buying accounts. Enable two-factor authentication where available. Never share your account credentials with anyone.',
    },
    {
      icon: 'ri-bank-card-line',
      title: 'Payment Awareness',
      desc: 'Use secure payment methods with buyer protection. Be cautious of sellers requesting payment outside the official platform. Review payment policies before completing transactions.',
    },
    {
      icon: 'ri-spam-3-line',
      title: 'Phishing Prevention',
      desc: 'Be wary of suspicious emails, messages, or links claiming to be from AllChinaBuy. Always access websites directly by typing the URL rather than clicking links in emails.',
    },
    {
      icon: 'ri-links-line',
      title: 'Link Safety',
      desc: 'Only use links from trusted sources. Avoid shortened URLs from unknown senders. When in doubt, search for the website directly rather than clicking provided links.',
    },
    {
      icon: 'ri-user-search-line',
      title: 'Seller Verification',
      desc: 'Research seller reputation before purchasing. Look for verified badges, transaction history, and customer feedback. Be cautious of sellers with limited or no reviews.',
    },
    {
      icon: 'ri-refund-line',
      title: 'Refund Awareness',
      desc: 'Familiarize yourself with the refund and return policies. Document all transactions and communications. Know your rights as a buyer before making purchases.',
    },
    {
      icon: 'ri-truck-line',
      title: 'Shipping Risks',
      desc: 'Understand shipping timelines and potential delays. Use tracked shipping methods for valuable items. Be aware of customs regulations and potential import duties in your country.',
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <nav aria-label="Breadcrumb" className="border-b border-background-200">
          <div className="container-main py-3">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to={`/${currentLang}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                  {t('breadcrumbHome')}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-300 text-xs" /></li>
              <li className="text-foreground-700 font-medium">{t('breadcrumbSafety')}</li>
            </ol>
          </div>
        </nav>

        <section className="section-padding">
          <div className="container-main max-w-3xl mx-auto text-center mb-12">
            <div className="w-16 h-16 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-shield-check-line text-accent-600 text-2xl" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              {t('safetyCenter')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {t('safetyCenterDesc')}
            </p>
          </div>

          <div className="container-main">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {safetyTopics.map((topic) => (
                <div key={topic.title} className="card p-6 md:p-7">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${topic.icon} text-primary-600 text-lg`} />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">
                    {topic.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {topic.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-100">
          <div className="container-main max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              {t('safeBrowsingChecklist')}
            </h2>
            <div className="card p-6 md:p-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t('verifyHTTPS')}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t('verifyHTTPSDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t('strongPasswords')}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t('strongPasswordsDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t('twoFactorAuth')}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t('twoFactorAuthDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t('researchBeforeBuy')}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t('researchBeforeBuyDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t('keepRecords')}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t('keepRecordsDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t('reportSuspicious')}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t('reportSuspiciousDesc')}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-8 p-6 rounded-lg bg-primary-50 border border-primary-200">
              <p className="text-sm text-foreground-700 mb-2">
                <strong>Disclaimer:</strong> {t('safetyDisclaimer')}
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={`/${currentLang}`} className="btn-primary gap-2 text-sm">
                <i className="ri-home-line" />
                {t('backToHome')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-secondary gap-2 text-sm"
              >
                <i className="ri-external-link-line" />
                {t('visitMain')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}